import React, { useEffect, useState, useRef } from "react";
import { editFarmacia } from "../../../slices/thunks";
import { useDispatch } from "react-redux";
import { Col, Row, Input, Label, Form, Spinner, Button, Alert } from "reactstrap";
import { Link } from "react-router-dom";
import { uploadLogoFarmacia, deleteLogoFarmacia } from "../../../helpers/backend_helper";
import logoDefault from "../../../assets/images/companies/img-1.png";

const EditarFarmacia = ({ farmacia }) => {
  const dispatch = useDispatch();

  const [nombre, setNombre] = useState(farmacia.nombre || "");
  const [codigo_farmabien, setCodigoFarmabien] = useState(
    farmacia.codigo_farmabien || ""
  );
  const [codigo_myh, setCodigoMyh] = useState(farmacia.codigo_myh || "");
  const [direccion, setDireccion] = useState(farmacia.direccion || "");
  const [coordenadas, setCoordenadas] = useState(farmacia.coordenadas || "");
  const [loading, setLoading] = useState(false);

  // Función para limpiar errores del formulario
  const clearFormError = () => {
    if (formError) setFormError(null);
  };

  // Estados para logo
  const [logo, setLogo] = useState(farmacia.logo || null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoError, setLogoError] = useState(null);
  const fileInputRef = useRef(null);

  // Estados para el formulario
  const [formError, setFormError] = useState(null);

  // Función para manejar la subida de logo
  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setLogoError('Por favor selecciona una imagen válida');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setLogoError('La imagen no debe superar los 5MB');
      return;
    }

    setLogoError(null);

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64 = reader.result;
          // Mostrar vista previa inmediatamente
          setLogo(base64);
          setUploadingLogo(true);
          
          const response = await uploadLogoFarmacia(farmacia.id, base64);
          
          if (response?.data?.data?.logo) {
            // Actualizar con la URL del servidor
            setLogo(response.data.data.logo);
          }
        } catch (err) {
          setLogoError('Error al subir el logo');
          // Revertir a logo anterior si falla
          setLogo(farmacia.logo || null);
          console.error(err);
        } finally {
          setUploadingLogo(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setLogoError('Error al procesar la imagen');
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!logo) return;
    
    setUploadingLogo(true);
    try {
      await deleteLogoFarmacia(farmacia.id);
      setLogo(null);
    } catch (err) {
      setLogoError('Error al eliminar el logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  useEffect(() => {
    if (loading) {
      setTimeout(() => {
        setLoading(false);
      }, 3000);
    }
  }, [loading]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setFormError(null); // Limpiar error anterior

    const update = {
      id: farmacia.id,
      nombre: nombre,
      codigo_farmabien: codigo_farmabien,
      codigo_myh: codigo_myh,
      direccion:direccion,
      coordenadas:coordenadas,
      logo: logo, // Incluir el logo actual para no eliminarlo
      creado: farmacia.creado,
      actualizado: null,
    };

    try {
      const response = await dispatch(editFarmacia(update));
      
      if (response && response.status === 'success') {
        //console.log("Farmacia actualizada exitosamente");
        // Opcional: redirigir después de 2 segundos
        setTimeout(() => {
          window.location.href = '/farmacias';
        }, 2000);
      } else {
        console.error("Error inesperado al actualizar farmacia");
      }
    } catch (error) {
      console.error("Error al actualizar la Farmacia:", error);
      // Mostrar mensaje de error al usuario
      setFormError(error.message || 'Error al actualizar la farmacia');
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <Form onSubmit={handleSubmit}>
        {/* Sección de Logo */}
        <Row className="mb-4 p-3">
          <Col lg={12}>
            <div className="d-flex align-items-center gap-4">
              <div className="position-relative">
                <img
                  src={logo || logoDefault}
                  alt="Logo de farmacia"
                  className="rounded"
                  style={{ width: '100px', height: '100px', objectFit: 'contain', border: '1px solid #e9ebec' }}
                  onError={(e) => { e.target.src = logoDefault; }}
                />
                {uploadingLogo && (
                  <div 
                    className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rounded"
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                  >
                    <Spinner color="light" size="sm" />
                  </div>
                )}
              </div>
              <div>
                <h6 className="mb-2">Logo de la Farmacia</h6>
                <p className="text-muted mb-2 small">JPG, PNG o GIF. Máximo 5MB.</p>
                <div className="d-flex gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleLogoChange}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingLogo}
                  >
                    <i className="ri-upload-2-line me-1"></i>
                    Subir Logo
                  </Button>
                  {logo && (
                    <Button
                      color="danger"
                      size="sm"
                      outline
                      onClick={handleDeleteLogo}
                      disabled={uploadingLogo}
                    >
                      <i className="ri-delete-bin-line me-1"></i>
                      Eliminar
                    </Button>
                  )}
                </div>
                {logoError && (
                  <Alert color="danger" className="mt-2 mb-0 py-1 px-2">
                    {logoError}
                  </Alert>
                )}
              </div>
            </div>
          </Col>
        </Row>
        <hr className="mb-3" />
        
        {/* Error del formulario */}
        {formError && (
          <Row className="mb-3">
            <Col lg={12}>
              <Alert color="danger" className="mb-0">
                {formError}
              </Alert>
            </Col>
          </Row>
        )}
        
        <Row lg={12} className="p-3">
          <Col xxl={6} md={6}>
            <div className="mb-3">
              <Label className="form-label" htmlFor="gen-info-email-input">
                Nombre <span className="text-danger">*</span>
              </Label>
            
            <Input
              required
              minLength={3}
              maxLength={50}
              type="text"
              className="form-control"
              id="nombre"
              name="nombre"
              value={nombre}
              onChange={(e) => {
                setNombre(e.target.value);
                clearFormError();
              }}
            />
            </div>
          </Col>

          <Col xxl={6} md={6}>
             <div className="mb-3">
                <Label className="form-label" htmlFor="codigo_farmabien">
                Código Farmacia <span className="text-danger">*</span>
                </Label>
              
              <Input
                required
                minLength={3}
                maxLength={50}
                type="text"
                className="form-control"
                id="codigo_farmabien"
                name="codigo_farmabien"
                value={codigo_farmabien}
                onChange={(e) => {
                  setCodigoFarmabien(e.target.value);
                  clearFormError();
                }}
              />
              </div>
            </Col>
            <Col xxl={6} md={6}>
              <div className="mb-3">
                <Label className="form-label" htmlFor="codigo_myh">
                Código MyH+ <span className="text-danger">*</span>
                </Label>
               
              <Input
                required
                minLength={3}
                maxLength={50}
                type="text"
                className="form-control"
                id="codigo_myh"
                name="codigo_myh"
                value={codigo_myh}
                onChange={(e) => {
                  setCodigoMyh(e.target.value);
                  clearFormError();
                }}
              />
              </div>
            </Col>

            <Col xxl={6} md={6}>
              <div className="mb-3">
                <Label className="form-label" htmlFor="coordenadas">
                Coordenadas <span className="text-danger">*</span>
                </Label>
             
              <Input
                required
                minLength={3}
                maxLength={50}
                type="text"
                className="form-control"
                id="coordenadas"
                name="coordenadas"
                value={coordenadas}
                onChange={(e) => {
                  setCoordenadas(e.target.value);
                  clearFormError();
                }}
              />
               </div>
            </Col>
            <Col xxl={12} md={12}>
              <div className="mb-3">
                <Label className="form-label" htmlFor="direccion">
                Dirección <span className="text-danger">*</span>
                </Label>
                <Input
                  type="textarea"
                  required
                  className="form-control"
                  id="direccion"
                  name="direccion"
                  value={direccion}
                  onChange={(e) => {
                    setDireccion(e.target.value);
                    clearFormError();
                  }}
                />
              </div>
            </Col>
        </Row>
        <Row className="align-items-end">
          <Col lg={6}>
            <div className="mt-4 mb-3">
              <Link to="/farmacias">
                <Button className="btn btn-info btn-label left ms-auto nexttab nexttab">
                  <i className="ri-arrow-left-line label-icon align-middle fs-16 ms-2"></i>
                  Regresar
                </Button>
              </Link>
            </div>
          </Col>
          <Col lg={6}>
            <div className="mt-4 mb-3 text-end">
              <button
                type="submit"
                className="btn btn-success btn-label right ms-auto nexttab nexttab"
              >
                {loading ? (
                  <Spinner size="sm" className="me-2">
                    {" "}
                    Loading...{" "}
                  </Spinner>
                ) : null}
                <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2"></i>
                Actualizar
              </button>
            </div>
          </Col>
        </Row>
      </Form>
    </React.Fragment>
  );
};

export default EditarFarmacia;
